/**
 * DateTimePicker — 캘린더 날짜 + 시간 선택기
 *
 * DatePicker의 캘린더 로직을 재사용하고, 하단에 시/분 선택 UI를 추가한다.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '../../lib/cn.js'
import { toDateTime, formatISO, isSameDay, getCalendarDays } from './DatePicker.js'
import styles from './DateTimePicker.module.css'

export interface DateTimePickerProps {
  /** 선택된 날짜/시간 (ISO datetime 문자열 또는 Date) */
  value?: string | Date | null
  /** 날짜/시간 변경 콜백 */
  onChange?: (date: Date | null, isoString: string) => void
  /** 필드 레이블 */
  label?: string
  /** 에러 메시지 */
  error?: string
  /** 보조 안내 텍스트 */
  helperText?: string
  /** 플레이스홀더 */
  placeholder?: string
  /** 날짜+시간 포맷 (표시용) */
  displayFormat?: (date: Date) => string
  /** 최소 선택 가능 날짜 */
  minDate?: Date
  /** 최대 선택 가능 날짜 */
  maxDate?: Date
  /** 입력 크기 */
  size?: 'sm' | 'md'
  /** 비활성 상태 */
  disabled?: boolean
  /** 추가 클래스명 */
  className?: string
  /** 분 단위 간격 (기본 5) */
  minuteStep?: number
}

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토']

function defaultFormat(d: Date): string {
  const h = String(d.getHours()).padStart(2, '0')
  const m = String(d.getMinutes()).padStart(2, '0')
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일 ${h}:${m}`
}

function buildHourOptions(): { value: number; label: string }[] {
  const opts: { value: number; label: string }[] = []
  for (let h = 0; h < 24; h++) {
    const period = h < 12 ? '오전' : '오후'
    const display = h === 0 ? 12 : h > 12 ? h - 12 : h
    opts.push({ value: h, label: `${period} ${display}시` })
  }
  return opts
}

function buildMinuteOptions(step: number): { value: number; label: string }[] {
  const opts: { value: number; label: string }[] = []
  for (let m = 0; m < 60; m += step) {
    opts.push({ value: m, label: `${String(m).padStart(2, '0')}분` })
  }
  return opts
}

export function DateTimePicker({
  value,
  onChange,
  label,
  error,
  helperText,
  placeholder = '날짜와 시간을 선택하세요',
  displayFormat = defaultFormat,
  minDate,
  maxDate,
  size = 'md',
  disabled = false,
  className,
  minuteStep = 5,
}: DateTimePickerProps) {
  const [open, setOpen] = useState(false)
  const selected = toDateTime(value)
  const today = useMemo(() => new Date(), [])

  const [viewYear, setViewYear] = useState(() => (selected ?? today).getFullYear())
  const [viewMonth, setViewMonth] = useState(() => (selected ?? today).getMonth())

  // 시/분 상태 (선택된 값 또는 기본 12:00)
  const [hour, setHour] = useState(() => selected?.getHours() ?? 12)
  const [minute, setMinute] = useState(() => {
    if (!selected) return 0
    // minuteStep에 맞춰 반올림
    return Math.round(selected.getMinutes() / minuteStep) * minuteStep
  })

  const [hourInputValue, setHourInputValue] = useState(() => String(hour).padStart(2, '0'))
  const [minuteInputValue, setMinuteInputValue] = useState(() => String(minute).padStart(2, '0'))

  const wrapperRef = useRef<HTMLDivElement>(null)
  const hourListRef = useRef<HTMLDivElement>(null)
  const minuteListRef = useRef<HTMLDivElement>(null)

  const hourOptions = useMemo(() => buildHourOptions(), [])
  const minuteOptions = useMemo(() => buildMinuteOptions(minuteStep), [minuteStep])

  // 선택 값이 바뀌면 뷰/시간 추적
  useEffect(() => {
    if (selected) {
      setViewYear(selected.getFullYear())
      setViewMonth(selected.getMonth())
      const h = selected.getHours()
      const m = Math.round(selected.getMinutes() / minuteStep) * minuteStep
      setHour(h)
      setMinute(m)
      setHourInputValue(String(h).padStart(2, '0'))
      setMinuteInputValue(String(m).padStart(2, '0'))
    }
  }, [selected, minuteStep])

  const calendarDays = useMemo(() => getCalendarDays(viewYear, viewMonth), [viewYear, viewMonth])

  const isDisabledDate = useCallback((d: Date) => {
    if (minDate && d < new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate())) return true
    if (maxDate && d > new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate())) return true
    return false
  }, [minDate, maxDate])

  const emitChange = useCallback((date: Date, h: number, m: number) => {
    const result = new Date(date.getFullYear(), date.getMonth(), date.getDate(), h, m, 0, 0)
    onChange?.(result, result.toISOString())
  }, [onChange])

  const handlePrevMonth = useCallback(() => {
    setViewMonth((m) => {
      if (m === 0) { setViewYear((y) => y - 1); return 11 }
      return m - 1
    })
  }, [])

  const handleNextMonth = useCallback(() => {
    setViewMonth((m) => {
      if (m === 11) { setViewYear((y) => y + 1); return 0 }
      return m + 1
    })
  }, [])

  const handleSelectDay = useCallback((d: Date) => {
    if (isDisabledDate(d)) return
    emitChange(d, hour, minute)
  }, [isDisabledDate, emitChange, hour, minute])

  const handleHourSelect = useCallback((h: number) => {
    setHour(h)
    setHourInputValue(String(h).padStart(2, '0'))
    if (selected) {
      emitChange(selected, h, minute)
    }
  }, [selected, minute, emitChange])

  const handleMinuteSelect = useCallback((m: number) => {
    setMinute(m)
    setMinuteInputValue(String(m).padStart(2, '0'))
    if (selected) {
      emitChange(selected, hour, m)
    }
  }, [selected, hour, emitChange])

  const handleHourInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '')
    if (val.length <= 2) setHourInputValue(val)
  }, [])

  const handleHourInputBlur = useCallback(() => {
    let h = parseInt(hourInputValue, 10)
    if (isNaN(h)) h = hour
    h = Math.max(0, Math.min(23, h))
    setHour(h)
    setHourInputValue(String(h).padStart(2, '0'))
    if (selected) {
      emitChange(selected, h, minute)
    }
  }, [hourInputValue, hour, selected, minute, emitChange])

  const handleMinuteInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '')
    if (val.length <= 2) setMinuteInputValue(val)
  }, [])

  const handleMinuteInputBlur = useCallback(() => {
    let m = parseInt(minuteInputValue, 10)
    if (isNaN(m)) m = minute
    m = Math.max(0, Math.min(59, m))
    // minuteStep에 맞춰 반올림
    m = Math.round(m / minuteStep) * minuteStep
    if (m >= 60) m = 60 - minuteStep
    setMinute(m)
    setMinuteInputValue(String(m).padStart(2, '0'))
    if (selected) {
      emitChange(selected, hour, m)
    }
  }, [minuteInputValue, minute, minuteStep, selected, hour, emitChange])

  const handleToggle = useCallback(() => {
    if (!disabled) setOpen((v) => !v)
  }, [disabled])

  // 외부 클릭 시 닫기
  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  // ESC 키로 닫기
  useEffect(() => {
    if (!open) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open])

  // popover 열릴 때 선택된 항목으로 스크롤
  useEffect(() => {
    if (!open) return
    requestAnimationFrame(() => {
      hourListRef.current?.querySelector(`.${styles.timeItemSelected}`)
        ?.scrollIntoView({ block: 'center' })
      minuteListRef.current?.querySelector(`.${styles.timeItemSelected}`)
        ?.scrollIntoView({ block: 'center' })
    })
  }, [open])

  return (
    <div ref={wrapperRef} className={cn(styles.wrapper, className)}>
      {label && <label className={styles.label}>{label}</label>}

      <button
        type="button"
        className={cn(
          styles.trigger,
          styles[size],
          disabled && styles.disabled,
          error && styles.triggerError,
        )}
        onClick={handleToggle}
        disabled={disabled}
      >
        <span className={cn(styles.triggerText, !selected && styles.placeholder)}>
          {selected ? displayFormat(selected) : placeholder}
        </span>
        <svg className={styles.calendarIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="3" y="4" width="18" height="18" rx="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M16 2v4M8 2v4M3 10h18" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className={styles.popover}>
          <div className={styles.popoverBody}>
            <div className={styles.calendarArea}>
              {/* 헤더: 월 이동 */}
              <div className={styles.calendarHeader}>
                <button type="button" className={styles.navBtn} onClick={handlePrevMonth}>
                  <svg viewBox="0 0 16 16" fill="currentColor" width="14" height="14">
                    <path d="M10.354 3.646a.5.5 0 010 .708L6.707 8l3.647 3.646a.5.5 0 01-.708.708l-4-4a.5.5 0 010-.708l4-4a.5.5 0 01.708 0z" />
                  </svg>
                </button>
                <span className={styles.monthTitle}>
                  {viewYear}년 {viewMonth + 1}월
                </span>
                <button type="button" className={styles.navBtn} onClick={handleNextMonth}>
                  <svg viewBox="0 0 16 16" fill="currentColor" width="14" height="14">
                    <path d="M5.646 3.646a.5.5 0 01.708 0l4 4a.5.5 0 010 .708l-4 4a.5.5 0 01-.708-.708L9.293 8 5.646 4.354a.5.5 0 010-.708z" />
                  </svg>
                </button>
              </div>

              {/* 요일 헤더 */}
              <div className={styles.weekdays}>
                {WEEKDAYS.map((w) => (
                  <span key={w} className={styles.weekday}>{w}</span>
                ))}
              </div>

              {/* 날짜 그리드 */}
              <div className={styles.daysGrid}>
                {calendarDays.map(({ date, currentMonth }, i) => {
                  const isSelected = selected ? isSameDay(date, selected) : false
                  const isToday = isSameDay(date, today)
                  const isDayDisabled = isDisabledDate(date)

                  return (
                    <button
                      key={i}
                      type="button"
                      className={cn(
                        styles.dayCell,
                        !currentMonth && styles.dayOtherMonth,
                        isToday && styles.dayToday,
                        isSelected && styles.daySelected,
                        isDayDisabled && styles.dayDisabled,
                      )}
                      onClick={() => handleSelectDay(date)}
                      disabled={isDayDisabled}
                    >
                      {date.getDate()}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* 시간 선택 */}
            <div className={styles.timeSection}>
              {/* 시 컬럼 */}
              <div className={styles.timeColumn}>
                <input
                  type="text"
                  inputMode="numeric"
                  className={styles.timeInput}
                  value={hourInputValue}
                  onChange={handleHourInputChange}
                  onBlur={handleHourInputBlur}
                  maxLength={2}
                />
                <div className={styles.timeList} ref={hourListRef}>
                  {hourOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={cn(styles.timeItem, hour === opt.value && styles.timeItemSelected)}
                      onClick={() => handleHourSelect(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              {/* 분 컬럼 */}
              <div className={styles.timeColumn}>
                <input
                  type="text"
                  inputMode="numeric"
                  className={styles.timeInput}
                  value={minuteInputValue}
                  onChange={handleMinuteInputChange}
                  onBlur={handleMinuteInputBlur}
                  maxLength={2}
                />
                <div className={styles.timeList} ref={minuteListRef}>
                  {minuteOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={cn(styles.timeItem, minute === opt.value && styles.timeItemSelected)}
                      onClick={() => handleMinuteSelect(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && <span className={styles.errorText}>{error}</span>}
      {!error && helperText && <span className={styles.helperText}>{helperText}</span>}
    </div>
  )
}
